import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowRight, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { api, STORE } from "../lib/api";
import { money } from "../lib/shop";
import { ProductCard } from "../components/ProductCard";
import { HeroCarousel } from "../components/HeroCarousel";

const dealText = (d) => {
    const core =
        d.type === "percentage" ? `${d.value}% off`
        : d.type === "bxgy" ? `Buy ${d.buy_qty} get ${d.free_qty} free`
        : `${money(d.value)} off`;
    const scope = d.min_spend
        ? ` when you spend ${money(d.min_spend)}`
        : d.applies_to && d.applies_to !== "all"
          ? " on selected items"
          : " storewide";
    return core + scope;
};

export default function Home() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [banners, setBanners] = useState([]);
    const [deals, setDeals] = useState([]);

    useEffect(() => {
        api.get("/products")
            .then((r) => {
                const featured = r.data.filter((p) => p.featured);
                setProducts((featured.length ? featured : r.data).slice(0, 6));
            })
            .catch(() => {});
        api.get("/categories").then((r) => setCategories(r.data.filter((c) => c.name !== "Meat & Poultry"))).catch(() => {});
        api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
        api.get("/shop/deals").then((r) => setDeals(r.data)).catch(() => {});
    }, []);

    const copyCode = (code) => {
        navigator.clipboard?.writeText(code).then(
            () => toast.success(`Code ${code} copied — use it at checkout`),
            () => toast.info(`Use code ${code} at checkout`)
        );
    };

    return (
        <main data-testid="home-page">
            {/* Hero — auto-playing carousel: storefront, admin photo banners, live deals */}
            <HeroCarousel banners={banners} deals={deals} />

            {/* Active deals — discount codes customers can use at checkout (writing only, no box) */}
            {deals.length > 0 && (
                <section data-testid="deals-banner">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <ul className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
                            {deals.map((d) => (
                                <li key={d.code} className="flex items-center gap-2 text-[#0033A0]">
                                    <BadgePercent className="h-5 w-5 shrink-0" />
                                    <span className="font-heading font-bold text-sm md:text-base">
                                        {dealText(d)}
                                        {d.ends_at && (
                                            <span className="font-normal text-xs md:text-sm text-slate-500">
                                                {" "}· ends {new Date(d.ends_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                                            </span>
                                        )}
                                        {" "}· use code{" "}
                                        <button
                                            type="button"
                                            onClick={() => copyCode(d.code)}
                                            data-testid={`deal-copy-${d.code}`}
                                            title={`Copy code ${d.code}`}
                                            className="font-mono font-bold tracking-wider underline decoration-dashed decoration-2 underline-offset-4 hover:text-[#40E0D0] transition-colors"
                                        >
                                            {d.code}
                                        </button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* What we offer — auto-scrolling ticker */}
            <section className="bg-[#0033A0] py-5 overflow-hidden" data-testid="offerings-strip">
                <div className="ticker-track text-white font-heading font-semibold text-sm md:text-base tracking-wide">
                    {[0, 1].map((copy) => (
                        <ul key={copy} aria-hidden={copy === 1} className="flex shrink-0 items-center gap-x-10 pr-10" data-testid={copy === 0 ? "offerings-list" : undefined}>
                            {["Fruit & Vegetables", "Butcher", "PayPoint", "International Grocery", "Afro Caribbean Food", "Fresh Fish", "Halal Meat", "World Foods"].map((item) => (
                                <li key={item} className="flex items-center gap-2 whitespace-nowrap">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#40E0D0]" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    ))}
                </div>
            </section>

            {/* Promotional banners (managed in the admin portal) — text-only ones; photo banners appear in the hero carousel */}
            {banners.some((b) => !b.image) && (
                <section className="bg-white pt-10" data-testid="promo-banners">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
                        {banners.filter((b) => !b.image).map((b) => (
                            <div key={b.id} data-testid={`promo-banner-${b.id}`}
                                className="rounded-2xl bg-[#0033A0] px-6 py-6 md:px-10 md:py-8 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                                <div>
                                    <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white">{b.title}</h2>
                                    {b.text && <p className="text-blue-100 mt-1">{b.text}</p>}
                                </div>
                                {b.cta_label && b.cta_url && (
                                    <Link to={b.cta_url} data-testid={`promo-cta-${b.id}`}
                                        className="shrink-0 inline-flex items-center rounded-full bg-[#40E0D0] px-7 py-3 font-heading font-bold text-[#0033A0] hover:bg-[#2fd4c4] transition-colors">
                                        {b.cta_label}
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Featured products */}
            <section className="py-16 md:py-24 bg-white" data-testid="featured-products-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                        <div>
                            <p className="font-heading font-bold text-[#0033A0] uppercase tracking-widest text-sm" data-testid="featured-eyebrow">
                                From our butcher counter
                            </p>
                            <h2 className="turquoise-underline font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mt-2" data-testid="featured-heading">
                                Fresh Meat & Poultry
                            </h2>
                        </div>
                        <Link
                            to="/products"
                            data-testid="featured-view-all"
                            className="inline-flex items-center gap-2 font-heading font-bold text-[#0033A0] hover:gap-3 transition-all"
                        >
                            View all products <ArrowRight className="h-5 w-5" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8" data-testid="featured-products-grid">
                        {products.map((p) => (
                            <ProductCard key={p.id} product={p} testId={`featured-product-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="py-16 md:py-24 bg-slate-50" data-testid="categories-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="font-heading font-bold text-[#0033A0] uppercase tracking-widest text-sm" data-testid="categories-eyebrow">
                        Aisles full of flavour
                    </p>
                    <h2 className="turquoise-underline font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mt-2 mb-10" data-testid="categories-heading">
                        More In Store
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="categories-grid">
                        {categories.map((c) => (
                            <div key={c.id} className="card-lift relative rounded-2xl overflow-hidden h-56 group" data-testid={`category-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                <Link to={`/products?category=${encodeURIComponent(c.name)}`} className="absolute inset-0 z-10" aria-label={`Browse ${c.name}`} />
                                <img
                                    src={c.image}
                                    alt={c.name}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#00206b]/90 via-[#0033A0]/30 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-5">
                                    <h3 className="font-heading font-bold text-white text-xl">{c.name}</h3>
                                    <p className="text-blue-100 text-sm mt-1">{c.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About preview */}
            <section className="py-16 md:py-24 bg-white" data-testid="about-preview-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                        <img
                            src="/assets/storefront.png"
                            alt="The front of Freddie's Afro Caribbean Store"
                            loading="lazy"
                            className="w-full h-full object-cover"
                            data-testid="about-preview-image"
                        />
                    </div>
                    <div>
                        <p className="font-heading font-bold text-[#0033A0] uppercase tracking-widest text-sm">About Us</p>
                        <h2 className="turquoise-underline font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mt-2 mb-8" data-testid="about-preview-heading">
                            A Local Reading Favourite
                        </h2>
                        <p className="text-slate-600 leading-relaxed" data-testid="about-preview-text">
                            Freddie's Afro Caribbean Store on Whitley Street is your neighbourhood shop for African and
                            Caribbean groceries, fresh halal meat from our butcher counter, and fresh fruit and
                            vegetables. We also stock a wide range of international food products, and PayPoint is
                            available in store.
                        </p>
                        <Link
                            to="/about"
                            data-testid="about-preview-link"
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0033A0] px-7 py-3 font-heading font-bold text-white transition-colors hover:bg-[#002277]"
                        >
                            Read our story <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Visit us */}
            <section className="py-16 md:py-24 bg-[#0033A0]" data-testid="visit-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="font-heading font-bold text-[#40E0D0] uppercase tracking-widest text-sm">Visit Us</p>
                        <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mt-2 mb-6" data-testid="visit-heading">
                            Come and Say Hello
                        </h2>
                        <ul className="space-y-4 text-blue-100">
                            <li className="flex items-start gap-3" data-testid="visit-address">
                                <MapPin className="h-5 w-5 mt-0.5 text-[#40E0D0] shrink-0" />
                                <span>{STORE.address}</span>
                            </li>
                            {STORE.phones.map((p) => (
                                <li key={p.tel} className="flex items-center gap-3">
                                    <Phone className="h-5 w-5 text-[#40E0D0] shrink-0" />
                                    <a href={`tel:${p.tel}`} data-testid={`visit-phone-${p.tel.slice(-4)}`} className="hover:text-[#40E0D0] transition-colors">
                                        {p.label}
                                    </a>
                                </li>
                            ))}
                            {STORE.hours.map((h) => (
                                <li key={h.days} className="flex items-start gap-3">
                                    <Clock className="h-5 w-5 mt-0.5 text-[#40E0D0] shrink-0" />
                                    <span>
                                        <span className="font-bold text-white">{h.days}:</span> {h.time}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <a
                            href={STORE.directionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="visit-directions-button"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#40E0D0] px-8 py-3.5 font-heading font-bold text-[#0033A0] transition-colors hover:bg-[#2fd4c4]"
                        >
                            <MapPin className="h-5 w-5" />
                            Get Directions
                        </a>
                    </div>
                    <div className="rounded-2xl overflow-hidden shadow-xl h-72 md:h-96 border-4 border-white/10">
                        <iframe
                            title="Map showing Freddie's Afro Caribbean Store, 33–35 Whitley Street, Reading"
                            src={STORE.mapEmbedUrl}
                            className="h-full w-full"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="visit-map"
                        />
                    </div>
                </div>
            </section>
        </main>
    );
}
